import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RegionConfig } from 'src/environments/evironment.interface';

const REGION_STORAGE_KEY = 'region';

@Injectable({
  providedIn: 'root',
})
export class RegionConfigService {
  private readonly regionSubject = new BehaviorSubject<string>(this.getRegion());
  /** The current region code, re-emitting whenever it's changed through this service. */
  readonly region$: Observable<string> = this.regionSubject.pipe(distinctUntilChanged());

  setRegion(region: string): void {
    this.writeRegion(region);
    this.regionSubject.next(this.getRegion());
  }

  getRegion(): string {
    const storedRegion = this.readRegion();
    if (storedRegion) {
      const exists = environment.regions.some((config) => config.code === storedRegion);
      if (exists) {
        return storedRegion;
      }
      this.removeRegion();
    }
    return environment.defaultregion;
  }

  removeRegion(): void {
    this.clearRegion();
    this.regionSubject?.next(this.getRegion());
  }

  private isStorageAvailable(): boolean {
    return typeof localStorage !== 'undefined';
  }

  private readRegion(): string | null {
    if (!this.isStorageAvailable()) {
      return null;
    }
    try {
      return localStorage.getItem(REGION_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeRegion(region: string): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    try {
      localStorage.setItem(REGION_STORAGE_KEY, region);
    } catch {
      // Storage disabled, full, or blocked — region preference is not persisted.
    }
  }

  private clearRegion(): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    try {
      localStorage.removeItem(REGION_STORAGE_KEY);
    } catch {
      // Storage disabled or blocked — nothing to clear.
    }
  }

  getConfigForRegion(): RegionConfig {
    const region = this.getRegion();
    const config = environment.regions.find((config) => config.code === region);
    if (!config) {
      throw new Error('No region config found for region ' + region);
    }
    return config;
  }

  staffOnboardingLicensedStatesEnabled(): boolean {
    return this.getConfigForRegion().staff_onboarding_licensed_states ?? false;
  }
}
